import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import ProtectedRoute from './components/ProtectedRoute'
import OnboardingGuard from './components/OnboardingGuard'
import SuperAdminRoute from './components/SuperAdminRoute'
import Navigation from './components/Navigation'
import ErrorBoundary from './components/ErrorBoundary'
import './utils/logger' // Initialize logger
import Home from './pages/Home'
import Login from './pages/Login'
import Signup from './pages/Signup'
import ConfirmEmail from './pages/ConfirmEmail'
import AuthConfirm from './pages/AuthConfirm'
import AcceptInvite from './pages/AcceptInvite'
import Dashboard from './pages/Dashboard'
import Assessment from './pages/Assessment'
import Assessments from './pages/Assessments'
import AssessmentDetail from './pages/AssessmentDetail'
import Customers from './pages/Customers'
import CustomerDetail from './pages/CustomerDetail'
import Team from './pages/Team'
import Settings from './pages/Settings'
import SharedAssessment from './pages/SharedAssessment'
import AdminDashboard from './pages/AdminDashboard'
import RebateConfig from './pages/RebateConfig'
import Analytics from './pages/Analytics'
import SuperAdmin from './pages/SuperAdmin'
import ActivityLog from './pages/ActivityLog'
import LogsViewer from './pages/LogsViewer'

// Layout wrapper to conditionally show navigation
function AppLayout({ children }) {
  const location = useLocation()
  // Don't show navigation on public pages or shared assessment pages
  const publicPages = ['/', '/login', '/signup', '/confirm-email', '/auth/confirm']
  const isSharedPage = location.pathname.startsWith('/share/')
  const isAcceptInvitePage = location.pathname.startsWith('/accept-invite/')
  const isSuperAdminPage = location.pathname.startsWith('/super-admin')
  const showNav = !publicPages.includes(location.pathname) && !isSharedPage && !isAcceptInvitePage && !isSuperAdminPage

  return (
    <>
      {showNav && <Navigation />}
      {children}
    </>
  )
}

function App() {
  return (
    <ErrorBoundary name="App">
      <Router>
        <AuthProvider>
          <AppLayout>
            <div className="app">
              <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/confirm-email" element={<ConfirmEmail />} />
              <Route path="/auth/confirm" element={<AuthConfirm />} />
              <Route path="/accept-invite/:token" element={<AcceptInvite />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Dashboard />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assessment"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Assessment />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/team"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Team />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assessments"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Assessments />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/assessments/:id"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <AssessmentDetail />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <Customers />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/customers/:id"
                element={
                  <ProtectedRoute>
                    <OnboardingGuard>
                      <CustomerDetail />
                    </OnboardingGuard>
                  </ProtectedRoute>
                }
              />
              {/* Admin routes */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/rebates"
                element={
                  <ProtectedRoute>
                    <RebateConfig />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/analytics"
                element={
                  <ProtectedRoute>
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/logs"
                element={
                  <ProtectedRoute>
                    <LogsViewer />
                  </ProtectedRoute>
                }
              />
              {/* Public shared assessment route - no authentication required */}
              <Route path="/share/:token" element={<SharedAssessment />} />
              {/* Super Admin routes - require super admin privileges */}
              <Route
                path="/super-admin"
                element={
                  <SuperAdminRoute>
                    <SuperAdmin />
                  </SuperAdminRoute>
                }
              />
              <Route
                path="/super-admin/activity-log"
                element={
                  <SuperAdminRoute>
                    <ActivityLog />
                  </SuperAdminRoute>
                }
              />
              </Routes>
            </div>
          </AppLayout>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  )
}

export default App
